import { observable, toJS } from 'mobx';
import { observer } from 'mobx-react';
import { PlusOutlined } from '@ant-design/icons';
import { PureComponent } from 'react';

import './style.scss';
import withNavigateParams from '../../utils/withNavigateParams';
import { NavigateFunction, Outlet } from 'react-router';
import { Sorting } from '../../web-types/Sorting';
import { admin_createPlace, admin_fetchChains, admin_fetchPlaces } from '../../lib/api-web';
import { Button } from 'antd';
import PromptModal from '../../global-modals/PromptModal';
import handleError, { handleResponseError } from '../../utils/handleError';
import { TableController } from '../../controls/TableController';
import { autobind } from 'core-decorators';
import { EntitySelect } from '../../controls/EntitySelect';

@observer
class AdminPlacesPage extends PureComponent<{ navigate: NavigateFunction; params: any; state: any }> {
	@observable loading = false;
	@observable placeAdding = false;
	@observable contentVisible = false;
	@observable total: number | null = null;
	@observable params: { query: string; byChain: number | null } = { query: '', byChain: null };

	@autobind
	handleTablePropsUpdate(contentVisible: boolean, total: number | null, rowsPerPage: number) {
		this.contentVisible = contentVisible;
		this.total = total;
	}

	@autobind
	async handleLoad(
		params: { query: string; byChain: number | null },
		offset: number,
		limit: number,
		sorting: Sorting | null,
	) {
		return admin_fetchPlaces(offset, limit, {
			query: params.query,
			sorting,
			placeId: this.props.params.placeId,
			chainId: params.byChain,
		});
	}

	async handleAddPlace() {
		const placeName = await PromptModal.prompt('Enter place name', '');
		if (placeName) {
			this.placeAdding = true;
			try {
				const response = await admin_createPlace(placeName);
				if (response.result) {
					this.props.navigate(`/admin-places/${response.data}`, { state: { backable: true } });
				} else {
					handleResponseError(response);
				}
			} catch (err) {
				handleError(err);
			}
			this.placeAdding = false;
		}
	}

	render() {
		if (this.props.params.placeId) {
			return <Outlet />;
		}
		return (
			<div className="page">
				<div className="page-head">
					<div className="page-title">
						<h2>Places</h2>
					</div>
					<div className="page-actions">
						<EntitySelect
							placeholder="Filter by chain"
							loader={async (query, id) => {
								const resp = await admin_fetchChains(0, 1000, {
									query,
									sorting: { key: 'name', direction: 'ASC' },
								});
								if (!resp.result) {
									return [];
								}
								return resp.data.rows.map(r => ({
									id: r.id,
									title: r.name,
									avatarUrl: r.logoUrl,
								}));
							}}
							selectedId={this.params.byChain}
							onSelect={id => {
								if (this.params.byChain !== id) {
									this.params = { ...toJS(this.params), byChain: id };
								}
							}}
						/>

						<Button
							type="primary"
							icon={<PlusOutlined />}
							onClick={e => {
								e.preventDefault();
								this.handleAddPlace();
							}}
						>
							Add place
						</Button>
					</div>
				</div>
				<TableController
					params={this.params}
					load={this.handleLoad}
					onRowClick={({ id }) =>
						this.props.navigate('/admin-places/' + id + '/', { state: { backable: true } })
					}
					columns={[
						{
							key: 'logoUrl',
							title: '',
							render: value => (
								<img src={value} alt="" style={{ width: 50, height: 50, borderRadius: '50%' }} />
							),
							width: 50,
						},
						{
							key: 'name',
							title: 'Title',
							sortable: true,
						},
						{
							key: 'employeesCount',
							title: 'Employees',
							sortable: true,
						},
						{
							key: 'tipsVolume7d',
							title: '7d tips volume',
							sortable: true,
						},
					]}
				/>
			</div>
		);
	}
}

export default withNavigateParams(AdminPlacesPage);
